import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Button, Grid, Typography } from "@material-ui/core";
// removed BreadcrumbBack (no longer needed)

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3, 3, 0, 3),
		[theme.breakpoints.up("sm")]: {
			padding: theme.spacing(4, 3, 4, 3),
		},
	},
	title: {
		paddingBottom: theme.spacing(0.5),
		fontSize: theme.typography.h3.fontSize,
		lineHeight: theme.typography.h3.lineHeight,
		letterSpacing: theme.typography.h3.letterSpacing,
		fontWeight: "bold",
		// On web, page titles should read clearly above sidebar nav (bold ~14px on highlight pills).
		[theme.breakpoints.up("sm")]: {
			fontSize: theme.typography.h2.fontSize,
			lineHeight: theme.typography.h2.lineHeight,
			letterSpacing: theme.typography.h2.letterSpacing,
		},
	},
	titleRow: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
		gap: theme.spacing(1.25),
		paddingBottom: theme.spacing(0.5),
	},
	titleInline: {
		paddingBottom: 0,
	},
	subtitle: {
		margin: 0,
		marginTop: theme.spacing(0.5),
		fontWeight: 400,
		fontSize: "15px",
		lineHeight: 1.4,
		color: theme.palette.text.secondary,
	},
	message: {
		padding: theme.spacing(0, 0),
		[theme.breakpoints.up("sm")]: {
			padding: theme.spacing(2, 0),
		},
	},
	breadcrumbs: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignItems: "flex-start",
	},
	breadcrumb: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignItems: "flex-start",
	},
	breadcrumbBreak: {
		display: "flex",
		padding: theme.spacing(0, 0.5),
	},
}));

/*
  breadcrumbs params example: 
    const breadcrumbs = [{
    label: `Dashboard`,
    value: HOME_ROUTE,
  }]
*/
const PageHeader = ({
    children,
    className,
    canGoBack, // accepted but unused to avoid leaking to DOM
	breadcrumbs,
	title,
	titleAdornment,
	hideTitle,
	subtitle,
    message,
    action,
    actionColor = "primary",
    onAction,
    backActionPath, // accepted but unused to avoid leaking to DOM
    ...rest
}) => {
	const classes = useStyles();
	const breadcrumbLinks = breadcrumbs && Array.isArray(breadcrumbs);

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<Grid
				alignItems="flex-end"
				container
				justify="space-between"
				spacing={3}
			>
				<Grid item xs={titleAdornment ? 12 : undefined}>
                    {/* Back button removed */}
					{breadcrumbLinks ? (
						<div className={classes.breadcrumbs}>
							{breadcrumbs.map(({ label, value }, index) => (
								<div key={index} className={classes.breadcrumb}>
									<RouterLink to={value}>
										<Typography
											component="h2"
											gutterBottom
											variant="overline"
										>
											{label}
										</Typography>
									</RouterLink>
									{index < breadcrumbs.length - 1 && (
										<Typography
											className={classes.breadcrumbBreak}
											component="h2"
											gutterBottom
											variant="overline"
										>
											/
										</Typography>
									)}
								</div>
							))}
						</div>
					) : (
						breadcrumbs != null && breadcrumbs !== "" && (
						<Typography
							component="h2"
							gutterBottom
							variant="overline"
						>
							{breadcrumbs}
						</Typography>
						)
					)}
					{!hideTitle && titleAdornment && (
						<div className={classes.titleRow}>
							{typeof title === "string" ? (
								<Typography
									component="h1"
									variant="h3"
									className={clsx(classes.title, classes.titleInline)}
								>
									{title}
								</Typography>
							) : (
								title
							)}
							{titleAdornment}
						</div>
					)}
					{!hideTitle && !titleAdornment &&
						(typeof title === "string" ? (
							<Typography
								component="h1"
								variant="h3"
								className={classes.title}
							>
								{title}
							</Typography>
						) : (
							title
						))}
					{subtitle && (
						<Typography
							component="p"
							variant="body1"
							className={classes.subtitle}
						>
							{subtitle}
						</Typography>
					)}
					{message && (
						<Typography className={classes.message} variant="body1">
							{message}
						</Typography>
					)}
				</Grid>
				<Grid item>
					{action && onAction && (
						<Button
							color={actionColor}
							variant="contained"
							onClick={onAction}
						>
							{action}
						</Button>
					)}
				</Grid>
			</Grid>
			{children}
		</div>
	);
};

PageHeader.propTypes = {
	children: PropTypes.any,
    // canGoBack: PropTypes.bool, // removed
	className: PropTypes.string,
	breadcrumbs: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.object),
	]),
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	titleAdornment: PropTypes.node,
	hideTitle: PropTypes.bool,
	subtitle: PropTypes.string,
	message: PropTypes.string,
	action: PropTypes.string,
	actionColor: PropTypes.string,
	onAction: PropTypes.func,
    // backActionPath: PropTypes.string, // removed
};

export default PageHeader;
